import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import {
  activeNavId,
  DEFAULT_SETTINGS_TAB,
  formatRoute,
  loginRoute,
  NAV_DESTINATIONS,
  NO_SUGGESTION,
  readPastedAddress,
  searchRoute,
  suggestionKeyToCommand,
  type NavDestinationId,
} from '@visious/core';
import { useSuggestions } from './useSuggestions';
import { useRoute } from '../router/router';
import { navigate } from '../router/router';
import { isOffline, subscribeOffline } from '../platform/online';
import { usePopularDisabled } from '../platform/popularCapability';
import { useSession } from '../platform/session';
import { useSettings, writeSettings } from '../platform/settings';
import {
  applyTheme,
  resolveTheme,
  subscribeSystemPrefersLight,
  systemPrefersLight,
  type ResolvedTheme,
} from '../platform/theme';
import { systemClock } from '../platform/clock';
import { clearEmptyNotice, useImportFlow } from '../tabs/store';
import { askToImportTabs } from '../tabs/useTabImport';
import { fmt, text } from '../strings';

/**
 * The shell's logic, under R13: every decision here, nothing but rendering in `AppShell.tsx`.
 *
 * What comes from core is the destination table, the sign-in gate and every href (`NAV_DESTINATIONS`,
 * `formatRoute`, `activeNavId`) — so a TV nav is the same nav. What is added here is the two things
 * that are genuinely per-platform: the **label** (core ships no display strings, R7) and the icon,
 * which the `.tsx` picks by id.
 */

/**
 * R7's other side: the strings live on the platform that renders them, keyed by core's ids.
 *
 * Exported since 3a because the sign-in page names the destination it will return to, and "the place
 * you were heading" has to be spelled the same way there as it is in the nav.
 */
export function navLabels(): Readonly<Record<NavDestinationId, string>> {
  const words = text().shell.nav;
  return {
    popular: words.popular,
    subscriptions: words.subscriptions,
    playlists: words.playlists,
    history: words.history,
  };
}

export interface ShellNavItem {
  readonly id: NavDestinationId;
  readonly label: string;
  readonly href: string;
  /** `aria-current="page"` — and `null` for a video or a search, which no destination describes. */
  readonly current: boolean;
  /**
   * Account-gated (§8, §13.2) *and* nobody is signed in. It stays a real link on purpose: the page it
   * lands on explains what is needed, which is an affordance, whereas a dead control is the "shown
   * broken" §8 forbids. The lock and the title say so before the click.
   */
  readonly locked: boolean;
  readonly title: string | undefined;
}

/**
 * The header search box, including §8's `/search/suggestions`. It is a **combobox** rather than an
 * input with a list under it, and the difference is entirely in what the keyboard does — which is why
 * every key decision comes from `suggestionKeyToCommand` in core rather than from this file.
 */
export interface ShellSearchBinding {
  readonly value: string;
  readonly setValue: (value: string) => void;
  readonly submit: () => void;
  /** Rendered only while `open`; a stale list is dropped rather than shown (`suggestionsMatch`). */
  readonly suggestions: readonly string[];
  readonly open: boolean;
  /** {@link NO_SUGGESTION} when the raw text is what Enter would search for. */
  readonly activeIndex: number;
  /** Returns `true` when it claimed the key, so the `.tsx` knows whether to `preventDefault`. */
  readonly handleKey: (key: string) => boolean;
  readonly accept: (index: number) => void;
  readonly close: () => void;
  /** A focus/blur pair, because a list that vanishes on blur cannot be clicked. */
  readonly onFocus: () => void;
  /**
   * Whether the search is expanded out of its menu item. Collapsed, the header shows the centred
   * menu; expanded, the field takes the menu's place (and, on a narrow window, the whole bar). The
   * text survives a collapse — the state is about the *bar*, not the query.
   */
  readonly active: boolean;
  readonly activate: () => void;
  /** Escape past a closed list, a blur that landed nowhere in the field, or a completed search. */
  readonly deactivate: () => void;
  /**
   * The reader is on the results page, so the menu item that *is* the search carries the current
   * marker like any other destination. Not from `activeNavId`: search is deliberately no
   * `NavDestination` — its id would leak into the landing-page setting, and its route with an empty
   * query parses as `not-found` — so the one consumer computes the one fact itself.
   */
  readonly current: boolean;
}

/** The header's account affordance (§8's signed-out behaviour, seen from the frame). */
export interface ShellAccount {
  readonly href: string;
  readonly label: string;
  readonly title: string;
  readonly signedIn: boolean;
  /** On the sign-in page — the control is a destination, so it carries the current marker. */
  readonly current: boolean;
}

export interface ShellBinding {
  /**
   * The destinations to draw, already filtered: the reader's own hidden set, and Popular
   * withheld while the instance has the endpoint switched off. The pages stay reachable by URL —
   * hiding is a menu fact, not a gate.
   */
  readonly nav: readonly ShellNavItem[];
  /** The brand link — `/`, which is the landing redirect rather than a page of its own. */
  readonly brandHref: string;
  readonly search: ShellSearchBinding;
  readonly theme: {
    readonly resolved: ResolvedTheme;
    readonly toggle: () => void;
  };
  /**
   * Moving the reader's other Visious tabs into this one (`tabs/`). In the header rather than on the
   * watch page, because a tab with nothing playing is exactly the tab somebody wants to move videos
   * *into* — and it opens a confirmation rather than acting, since the press closes other windows.
   *
   * Only the press lives here. Everything the act needs to know is inside `PlaybackHost`, which this
   * model is rendered above, so the two sides meet at a module-level flag (`tabs/store.ts`).
   */
  readonly importTabs: {
    readonly label: string;
    readonly ask: () => void;
    /**
     * The press is out and the other tabs have not answered yet. The control holds a spinner and
     * takes no presses, which is the only place this wait can be shown: there is nothing else on
     * screen yet, and what comes next may be a dialog or may be one line.
     */
    readonly busy: boolean;
    /**
     * The one answer the control gives without opening anything: no other tab had videos to move.
     * `null` the rest of the time, the confirmation's own faces included.
     */
    readonly notice: string | null;
  };
  /**
   * The browser is certain there is no connection (4d). One statement in the frame rather than a
   * fifth state per screen: every page below is already failing with 4a's `network` copy, and this is
   * the sentence that says the twelve of them have one cause. False also covers "we cannot tell",
   * which is most of what `navigator.onLine` is worth — see `platform/online.ts`.
   */
  readonly offline: boolean;
  readonly account: ShellAccount;
  /** §8's Settings page (3e). In the header rather than the nav — see `NAV_DESTINATIONS`. */
  readonly settings: { readonly href: string; readonly label: string; readonly current: boolean };
  /**
   * The renewal prompt (2026-08-02): a session inside its warning window that cannot renew
   * itself — the sign-in-page option was left off — gets one sentence in the frame with the way to
   * the page that fixes it. `null` for a session that renews itself (`canSelfRenew`: the app acts
   * instead of asking), for one already dismissed this tab, and for everything else.
   */
  readonly sessionExpiry: {
    readonly text: string;
    readonly renewHref: string;
    readonly renewLabel: string;
    readonly dismiss: () => void;
    readonly dismissLabel: string;
  } | null;
}

/**
 * The header's dark↔light flip, **derived** rather than stored since 3e.
 *
 * It used to hold the resolved theme in state, which was right while this was the only place a theme
 * could change. The settings page is a second writer, and a copy would have gone stale the moment it
 * chose one — the icon saying "dark" over a light page. So the theme is now
 * `resolveTheme(choice, system)` over two reactive inputs: the choice from the settings store (shared,
 * cross-tab) and the system preference from its media query.
 *
 * The `applyTheme` effect covers the one case neither writer does: the *system* flipping while the
 * choice is `'system'`. A write goes through `applyThemeChoice`, and boot goes through `initTheme`.
 */
function useTheme(): ShellBinding['theme'] {
  const choice = useSettings().theme;
  const [prefersLight, setPrefersLight] = useState<boolean>(() => systemPrefersLight());

  useEffect(() => subscribeSystemPrefersLight(setPrefersLight), []);

  const resolved = resolveTheme(choice, prefersLight);
  useEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  const toggle = useCallback(() => {
    // An explicit choice, so it stops following the system — which is what pressing the toggle means.
    // The way *back* to `'system'` is the settings page, which is what 3e added.
    const next: ResolvedTheme = resolved === 'dark' ? 'light' : 'dark';
    writeSettings({ theme: next });
  }, [resolved]);

  return { resolved, toggle };
}

/**
 * The combobox, assembled from three parts that are each somebody else's: the *list* comes from
 * `useSuggestions` (debounced, staleness-checked), every *key decision* from
 * `suggestionKeyToCommand`, and only the three pieces of local state are here — is it open, which row
 * is highlighted, and what a submit means.
 *
 * `dismissed` is separate from "no suggestions" on purpose. Escape and a completed search have to
 * close the list *without* clearing the text, and without that flag the very next render would
 * reopen it: the query still matches, so the fetched list is still valid. Typing clears it, which is
 * what makes Escape-then-keep-typing behave.
 */
function useSearchBox(query: string, setQuery: (value: string) => void): Omit<ShellSearchBinding, 'current'> {
  /**
   * Starts **dismissed**, and that is a defect a browser found rather than a preference. On a results
   * page the box is seeded from the route, so a list that opens for whatever text is in it opens
   * *immediately on arrival* — covering the heading, the count and the whole filter bar with thirteen
   * suggestions for the search the reader is already looking at. Suggestions are a response to typing or
   * to focusing the box, never to a navigation.
   */
  const [dismissed, setDismissed] = useState(true);
  /**
   * Whether the bar is expanded out of its icon. Collapsed by default *including on a results page*:
   * the page's own heading states the query there, and a bar that arrives expanded would hide the
   * centred menu on every search the reader follows a link to.
   */
  const [active, setActive] = useState(false);
  const [activeIndex, setActiveIndexState] = useState(NO_SUGGESTION);
  /**
   * The highlighted row, mirrored into a ref — and this is the one non-obvious line in the file.
   *
   * CLAUDE.md's trap says a key handler that reads its position from React state is stale for every
   * repeat, and 2b fixed it in the feed grid by reading the origin out of the DOM instead: focus *is*
   * where the browser put it, so the DOM cannot lag. **That fix does not transfer here.** A combobox
   * marks its current row with `aria-activedescendant`, which nothing but this code writes — so the DOM
   * carries the same one-render-old value the closure does, and reading it back would be the same bug
   * wearing a different hat.
   *
   * Measured before it was fixed: three synchronous `↓` moved the highlight from nothing to row **1**
   * instead of row 2, and four `↑` from row 1 landed on row 0 rather than back on the raw text. Held
   * arrow keys repeat far faster than a render, so this is the ordinary case rather than a stress test.
   * A ref is written synchronously, which is exactly the property the handler needs.
   */
  const activeRef = useRef(NO_SUGGESTION);
  const setActiveIndex = useCallback((index: number) => {
    activeRef.current = index;
    setActiveIndexState(index);
  }, []);

  const suggestions = useSuggestions(dismissed ? '' : query);

  const open = !dismissed && suggestions.length > 0;

  const go = useCallback((text: string) => {
    const trimmed = text.trim();
    if (trimmed === '') return;
    setDismissed(true);
    setActiveIndex(NO_SUGGESTION);
    // A completed search collapses the bar: the results page states the query itself, and the menu
    // the expansion hid is what the reader navigates onward with.
    setActive(false);
    // A pasted link opens the thing it names rather than searching for its spelling (the job
    // Home's retired paste-a-link box did). `readPastedAddress`, not `readPastedLink`: the
    // bare-ID rule would read any 11-letter word as a video id, and "programming" must search.
    const pasted = readPastedAddress(trimmed);
    if (pasted !== null) {
      navigate(formatRoute(pasted));
      return;
    }
    // A fresh search from the header resets every filter, which is what typing new words means —
    // `searchRoute` supplies the defaults. Refining a search happens on the results page's own
    // controls, where the current query is the starting point.
    navigate(formatRoute(searchRoute(trimmed)));
  }, [setActiveIndex]);

  return {
    value: query,
    setValue: useCallback(
      (value: string) => {
        setQuery(value);
        // Typing is what reopens the list — see `dismissed` above.
        setDismissed(false);
        setActiveIndex(NO_SUGGESTION);
      },
      [setActiveIndex, setQuery],
    ),
    submit: useCallback(() => go(query), [go, query]),
    suggestions: open ? suggestions : [],
    open,
    activeIndex,
    handleKey: useCallback(
      (key: string) => {
        // From the ref, never from `activeIndex` — see `activeRef`.
        const command = suggestionKeyToCommand(key, {
          count: suggestions.length,
          activeIndex: activeRef.current,
          open,
        });
        if (command === null) return false;

        switch (command.kind) {
          case 'highlight':
            setActiveIndex(command.index);
            return true;
          case 'accept': {
            const text = suggestions[command.index];
            if (text === undefined) return false;
            // The box shows what is being searched for, not what was typed — the same rule the route
            // sync above follows, applied a moment earlier.
            setQuery(text);
            go(text);
            return true;
          }
          case 'submit':
            go(query);
            return true;
          case 'close':
            // Three-stage Escape, clear-on-Escape's own shape: the first closes the list, the
            // second empties the box, the third collapses the bar. Core claims Escape even with the
            // list closed precisely so the caller can decide what that means.
            if (!open) {
              if (query !== '') setQuery('');
              else setActive(false);
            }
            setDismissed(true);
            setActiveIndex(NO_SUGGESTION);
            return true;
        }
      },
      [go, open, query, setActiveIndex, setQuery, suggestions],
    ),
    accept: useCallback(
      (index: number) => {
        const text = suggestions[index];
        if (text === undefined) return;
        setQuery(text);
        go(text);
      },
      [go, setQuery, suggestions],
    ),
    close: useCallback(() => {
      setDismissed(true);
      setActiveIndex(NO_SUGGESTION);
    }, [setActiveIndex]),
    onFocus: useCallback(() => setDismissed(false), []),
    active,
    activate: useCallback(() => setActive(true), []),
    deactivate: useCallback(() => {
      setActive(false);
      setDismissed(true);
      setActiveIndex(NO_SUGGESTION);
    }, [setActiveIndex]),
  };
}

export function useShell(): ShellBinding {
  const route = useRoute();
  const session = useSession();
  const theme = useTheme();
  const hiddenMenu = useSettings().hiddenMenu;
  const popularDisabled = usePopularDisabled();
  /*
   * The server snapshot is `false` — "we cannot tell" — which is also what a build with no `navigator`
   * would answer. Claiming offline is a statement, and a statement made without having asked is the
   * one thing this value must never be.
   */
  const offline = useSyncExternalStore(subscribeOffline, isOffline, () => false);
  const importFlow = useImportFlow();

  /*
   * The "nothing to move" line goes on the next press anywhere, the line itself included: it answers
   * a press and asks nothing back, so the reader's next act is the reader done with it. `pointerdown`
   * rather than `click` for `ConfirmButton`'s reason, and a press on the control itself lands here
   * first — so pressing it again asks the tabs again rather than only dismissing the line.
   */
  useEffect(() => {
    if (importFlow !== 'empty') return;
    document.addEventListener('pointerdown', clearEmptyNotice);
    return () => {
      document.removeEventListener('pointerdown', clearEmptyNotice);
    };
  }, [importFlow]);

  const routeQuery = route.kind === 'search' ? route.search.q : '';
  const [query, setQuery] = useState(routeQuery);
  const [syncedTo, setSyncedTo] = useState(routeQuery);
  // Adjusting state during render rather than in an effect: navigating to a search must leave the box
  // showing what was searched for, and an effect would render one frame with the previous query.
  if (routeQuery !== syncedTo) {
    setSyncedTo(routeQuery);
    setQuery(routeQuery);
  }

  const searchBox = useSearchBox(query, setQuery);
  const search: ShellSearchBinding = { ...searchBox, current: route.kind === 'search' };
  const active = activeNavId(route);

  const facts = session.facts;

  const words = text().shell;
  const labels = navLabels();

  /*
   * The renewal prompt's dismissal is per tab and per *expiry*: keyed on the token's own
   * `expireEpochSec`, so dismissing this session's warning cannot swallow the next session's.
   */
  const [expiryDismissedFor, setExpiryDismissedFor] = useState<number | null>(() => {
    try {
      const raw = globalThis.sessionStorage.getItem(EXPIRY_DISMISSED_KEY);
      return raw === null ? null : Number(raw);
    } catch {
      return null;
    }
  });
  const showExpiry =
    session.ready &&
    facts.signedIn &&
    facts.expiringSoon &&
    !facts.canSelfRenew &&
    facts.expireEpochSec !== null &&
    facts.expireEpochSec !== expiryDismissedFor;
  const sessionExpiry = !showExpiry
    ? null
    : {
        text: words.sessionExpiry.text(
          fmt().published(facts.expireEpochSec, Math.floor(systemClock.nowEpochMs() / 1000)) ?? '',
        ),
        renewHref: LOGIN_HREF,
        renewLabel: words.sessionExpiry.renew,
        dismiss: () => {
          const expiry = facts.expireEpochSec;
          if (expiry === null) return;
          try {
            globalThis.sessionStorage.setItem(EXPIRY_DISMISSED_KEY, String(expiry));
          } catch {
            // Blocked storage only makes the dismissal die with this render — the button still works.
          }
          setExpiryDismissedFor(expiry);
        },
        dismissLabel: words.sessionExpiry.dismiss,
      };

  return {
    nav: NAV_DESTINATIONS
      // The reader's own hidden set, and Popular while the instance refuses it. Filtered
      // here rather than in the `.tsx` so the bottom bar and the header stay one list.
      .filter(
        (destination) =>
          !hiddenMenu.includes(destination.id) &&
          !(destination.id === 'popular' && popularDisabled),
      )
      .map((destination) => {
        /*
         * `session.ready` is half of this condition on purpose. The token is keyed per instance origin
         * and the instance is resolved asynchronously (§3), so for the first tick there is no answer —
         * and drawing five padlocks in that tick means a signed-in reader watches their own app lock and
         * unlock on every page load. Guessing "unlocked" is the cheaper mistake: the destination is now a
         * real gate that explains itself, so an early click loses nothing.
         */
        const locked = destination.requiresAuth && session.ready && !facts.signedIn;
        return {
          id: destination.id,
          label: labels[destination.id],
          href: formatRoute(destination.route),
          current: destination.id === active,
          locked,
          title: locked ? words.nav.lockedTitle(labels[destination.id]) : undefined,
        };
      }),
    brandHref: HOME_HREF,
    search,
    theme,
    importTabs: {
      label: text().tabs.open,
      ask: askToImportTabs,
      busy: importFlow === 'probing',
      notice: importFlow === 'empty' ? text().tabs.empty : null,
    },
    offline,
    account: {
      href: LOGIN_HREF,
      /*
       * Neutral until the session is *known*, for the same reason the padlocks are (above): the chip is
       * in the header of every page, and "Sign in" flashed at a signed-in reader on every load is the
       * app telling them they have been logged out. The username when the instance sent one — it is the
       * only "who am I" the API offers, and it arrives with the grant rather than from an endpoint.
       */
      label: !session.ready
        ? words.account.unknown
        : facts.signedIn
          ? (facts.username ?? words.account.unknown)
          : words.account.signIn,
      title: facts.signedIn ? words.account.signedInTitle : words.account.signedOutTitle,
      signedIn: facts.signedIn,
      current: route.kind === 'login',
    },
    settings: { href: SETTINGS_HREF, label: words.settings, current: route.kind === 'settings' },
    sessionExpiry,
  };
}

const EXPIRY_DISMISSED_KEY = 'visious.sessionExpiryDismissed';

const LOGIN_HREF = formatRoute(loginRoute());
/** `/` — the landing redirect. The brand keeps pointing here; the nav's items are all `/feed/*`. */
const HOME_HREF = formatRoute({ kind: 'home' });
/**
 * Deliberately **not** a `NAV_DESTINATIONS` entry. That list is the D-pad order and §8's page table —
 * five content destinations a reader moves between — and settings is not one of them: it is somewhere you
 * go, do a thing, and leave. Adding a sixth item would also put it in the TV's primary focus path, where
 * it is the least likely target. So it lives beside the theme toggle and the account chip, which are the
 * header's other two "about the app" controls.
 */
const SETTINGS_HREF = formatRoute({ kind: 'settings', tab: DEFAULT_SETTINGS_TAB });
